import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { fulfillmentApi } from '../../api/fulfillment-api';
import { FulfillmentError } from '../../api/errors';
import {
  overrideReasonLabels,
  overrideReasons,
  requirementLabel,
  type FulfillmentOrder,
  type OverrideReason,
  type PickInput,
  type PickResult,
} from '../../domain/fulfillment';
import { CameraScanner } from './CameraScanner';
import { cameraScanSupported } from './camera-support';
import { fulfillmentErrorMessage, isUnknownOutcome } from './error-message';
import type { FulfillmentReferences } from './use-fulfillment-references';
import { useScanFeedback } from './use-scan-feedback';

type Feedback =
  | { tone: 'accepted'; text: string }
  | { tone: 'rejected'; text: string }
  | { tone: 'unknown'; text: string; retry: PickInput };

function newKey(): string {
  return crypto.randomUUID();
}

/**
 * The scan-first input: keyboard-wedge scanners type the code and press
 * Enter; the camera and manual entry feed the same request. Every scan
 * carries an idempotency key that is reused if the network drops, so a
 * retry is never counted twice.
 */
export function ScanPanel({
  order,
  references,
  onPicked,
  targetLineId,
  onClearTarget,
}: {
  order: FulfillmentOrder;
  references: FulfillmentReferences;
  onPicked: (result: PickResult) => void;
  /** Scans go to this line only, e.g. chosen on a PDA; cleared by the page. */
  targetLineId?: string | undefined;
  onClearTarget?: (() => void) | undefined;
}) {
  const input = useRef<HTMLInputElement>(null);
  const feedbackSound = useScanFeedback();
  const [code, setCode] = useState('');
  const [quantity, setQuantity] = useState('');
  const [manual, setManual] = useState(false);
  const [reason, setReason] = useState<OverrideReason | ''>('');
  const [note, setNote] = useState('');
  const [camera, setCamera] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const targetLine = order.lines.find((line) => line.id === targetLineId);
  const targetLineName = targetLine
    ? requirementLabel(targetLine.requirement, {
        part: references.partName,
        stock: references.stockName,
      })
    : '';
  const hasStockLines = targetLine
    ? targetLine.requirement.type === 'STOCK'
    : order.lines.some((line) => line.requirement.type === 'STOCK');

  const pick = useMutation({
    mutationFn: (request: PickInput) => fulfillmentApi.pick(request),
    onSuccess: (result) => {
      const line = result.order.lines.find((candidate) => candidate.id === result.pick.lineId);
      const lineName = line
        ? requirementLabel(line.requirement, {
            part: references.partName,
            stock: references.stockName,
          })
        : 'the order';
      const what = result.pick.itemId ?? `${result.pick.quantity} × ${result.pick.code}`;
      setFeedback({
        tone: 'accepted',
        text: result.replayed ? `${what} was already recorded.` : `Picked ${what} for ${lineName}.`,
      });
      feedbackSound.accepted();
      setCode('');
      setQuantity('');
      setManual(false);
      setReason('');
      setNote('');
      onPicked(result);
    },
    onError: (error: unknown, request) => {
      if (isUnknownOutcome(error)) {
        setFeedback({
          tone: 'unknown',
          text: `The scan of ${request.code} could not be confirmed. Retry sends the same scan; it is never counted twice.`,
          retry: request,
        });
        feedbackSound.rejected();
        return;
      }
      const text = fulfillmentErrorMessage(error, 'The scan could not be recorded.');
      setFeedback({
        tone: 'rejected',
        text: error instanceof FulfillmentError ? `${request.code}: ${text}` : text,
      });
      feedbackSound.rejected();
    },
  });

  // Keep the scan field ready for the next scan: focused, with any rejected
  // code selected so the next scan replaces it rather than appending to it.
  useEffect(() => {
    if (camera || manual || pick.isPending) return;
    input.current?.focus();
    input.current?.select();
  }, [camera, manual, pick.isPending]);

  function submit(scanned: string): void {
    setFormError(null);
    const value = scanned.trim();
    if (!value) {
      setFormError('Scan or type a serial number or SKU.');
      return;
    }
    const qty = quantity ? Number.parseInt(quantity, 10) : undefined;
    if (qty !== undefined && (!Number.isInteger(qty) || qty <= 0)) {
      setFormError('Quantity must be a whole number above zero.');
      return;
    }
    if (manual && !reason) {
      setFormError('Say why the code was typed instead of scanned.');
      return;
    }
    if (manual && reason === 'OTHER' && !note.trim()) {
      setFormError('Explain the reason in the note.');
      return;
    }
    pick.mutate({
      orderId: order.id,
      lineId: targetLineId,
      code: value,
      quantity: qty,
      manualOverride: manual && reason ? { reason, note: note.trim() || undefined } : undefined,
      idempotencyKey: newKey(),
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    submit(code);
  }

  return (
    <section className="scan-panel" aria-labelledby="scan-heading">
      <h2 id="scan-heading">Scan</h2>
      {targetLine ? (
        <p className="scan-target" role="status">
          Scanning for <strong>{targetLineName}</strong>
          <button type="button" className="link-button" onClick={onClearTarget}>
            Any line
          </button>
        </p>
      ) : null}
      <form className="scan-form" onSubmit={handleSubmit} aria-label="Scan an item">
        <label className="scan-field">
          <span>{manual ? 'Serial number or SKU (typed)' : 'Serial number or SKU'}</span>
          <input
            ref={input}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            enterKeyHint="send"
            aria-describedby="scan-hint"
            // Read-only, not disabled, while a scan is in flight: a
            // disabled field loses focus and drops the next scan.
            readOnly={pick.isPending}
            aria-busy={pick.isPending}
          />
        </label>
        {hasStockLines ? (
          <label className="quantity-field">
            <span>Quantity</span>
            <input
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              placeholder="1"
              aria-label="Quantity for stock"
            />
          </label>
        ) : null}
        <button type="submit" className="primary" disabled={pick.isPending}>
          {pick.isPending ? 'Recording…' : manual ? 'Record typed code' : 'Record scan'}
        </button>
      </form>
      <p id="scan-hint" className="field-hint">
        Scanners type the code and press Enter. Quantity applies to stock only.
      </p>

      <div className="scan-tools">
        {cameraScanSupported() ? (
          <button type="button" onClick={() => setCamera((open) => !open)} aria-pressed={camera}>
            {camera ? 'Hide camera' : 'Scan with camera'}
          </button>
        ) : null}
        <button type="button" onClick={() => setManual((open) => !open)} aria-pressed={manual}>
          {manual ? 'Back to scanning' : 'Enter code by hand'}
        </button>
      </div>

      {manual ? (
        <fieldset className="manual-override">
          <legend>Why can’t it be scanned?</legend>
          {overrideReasons.map((candidate) => (
            <label key={candidate} className="radio-field">
              <input
                type="radio"
                name="override-reason"
                value={candidate}
                checked={reason === candidate}
                onChange={() => setReason(candidate)}
              />
              {overrideReasonLabels[candidate]}
            </label>
          ))}
          <label>
            Note{reason === 'OTHER' ? ' (required)' : ''}
            <input value={note} onChange={(event) => setNote(event.target.value)} />
          </label>
          <p className="field-hint">
            Typed codes are checked exactly like scans and recorded with your name.
          </p>
        </fieldset>
      ) : null}

      {camera ? (
        <CameraScanner
          onDetected={(detected) => {
            setCode(detected);
            submit(detected);
          }}
          onClose={() => setCamera(false)}
        />
      ) : null}

      {formError ? (
        <p role="alert" className="form-error">
          {formError}
        </p>
      ) : null}
      <div className="scan-feedback-region" aria-live="assertive" aria-atomic="true">
        {feedback ? (
          <div className={`scan-feedback scan-feedback-${feedback.tone}`}>
            <strong>
              {feedback.tone === 'accepted'
                ? 'Accepted'
                : feedback.tone === 'rejected'
                  ? 'Not accepted'
                  : 'Not confirmed'}
            </strong>
            <span>{feedback.text}</span>
            {feedback.tone === 'unknown' ? (
              <button
                type="button"
                onClick={() => pick.mutate(feedback.retry)}
                disabled={pick.isPending}
              >
                Retry the same scan
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
